import React from "react";
import {
  autoUpdate,
  flip,
  useFloating,
  type Placement,
} from "@floating-ui/react";
import { Popover } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { twJoin, twMerge } from "tailwind-merge";
import {
  CalendarIcon,
  ChevronIcon,
  ExclamationCircleIcon,
} from "@components/icons";
import { Tooltip } from "@components/Tooltip/Tooltip";
import { isNullOrUndefined } from "@utils/common";
import { formatDate } from "@utils/units";
import { MonthPickerContent } from "./components/MonthPickerContent";

export interface IMonthPickerAvailableDate {
  year: number;
  month: number;
  isError?: boolean;
  href?: string;
}

interface IMonthPickerProps {
  value: Date;
  onChange: (value: Date, dateInformation?: IMonthPickerAvailableDate) => void;
  placement?: Placement;
  isPositionFixed?: boolean;
  showWholeDate?: boolean;
  /**
   * Dates that are available to be selected
   */
  availableDates?: IMonthPickerAvailableDate[];
  disableBefore?: Date;
  disableFutureDates?: boolean;
  error?: string;
  customLabel?: string;
  className?: string;
}

export const MonthPicker: React.FC<IMonthPickerProps> = (props) => {
  const {
    value,
    onChange,
    placement,
    isPositionFixed,
    showWholeDate,
    availableDates,
    disableBefore,
    disableFutureDates,
    error,
    customLabel,
    className,
  } = props;
  const { t } = useTranslation();

  const { refs, floatingStyles } = useFloating({
    placement: placement ?? "bottom-end",
    strategy: isPositionFixed ? "fixed" : "absolute",
    middleware: [flip()],
    whileElementsMounted: autoUpdate,
  });

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <React.Fragment>
          <Popover.Button
            ref={refs.setReference}
            className={twMerge(
              "flex h-10 w-10 items-center justify-center gap-2 rounded-full border border-base-black text-sm font-semibold leading-5 text-base-black transition-shadow hover:border-grey-dark hover:bg-blue-hover lg:w-fit lg:justify-normal lg:border-grey-hover lg:bg-white lg:px-3 lg:hover:bg-white",
              open && "ring-4 ring-base-light-grey ring-opacity-100",
              className,
            )}
          >
            <CalendarIcon className="size-5 shrink-0 lg:size-4" />
            {!customLabel && (
              <span className="hidden lg:block">
                {formatDate(
                  value,
                  t(
                    showWholeDate
                      ? "common.dateFormat.yearMonthDay"
                      : "common.dateFormat.yearMonth",
                  ),
                )}
              </span>
            )}
            {customLabel && (
              <span className="hidden lg:block">{customLabel}</span>
            )}
            {!isNullOrUndefined(error) && (
              <Tooltip content={error}>
                {(props) => (
                  <span className={twJoin("text-red-dark")} {...props}>
                    <ExclamationCircleIcon className={"h-5 w-5"} />
                  </span>
                )}
              </Tooltip>
            )}
            <ChevronIcon
              className={twJoin(
                "hidden size-5 transition-transform lg:block",
                open && "rotate-180",
              )}
            />
          </Popover.Button>
          <Popover.Panel
            ref={refs.setFloating}
            style={floatingStyles}
            className="absolute z-30 mt-2 rounded-xl border border-grey-light bg-white shadow-lg"
          >
            <MonthPickerContent
              value={value}
              close={close}
              onChange={onChange}
              availableDates={availableDates}
              disableBefore={disableBefore}
              disableFutureDates={disableFutureDates}
            />
          </Popover.Panel>
        </React.Fragment>
      )}
    </Popover>
  );
};
